import React from 'react';
import { Grid, Card, CardContent, CardMedia, Typography, Box, IconButton, Chip } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, ContentCopy as CloneIcon } from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';
import { format } from 'date-fns';

function BookCard({ book, userCurrency, onEdit, onDelete, onClone, getImageUrl }) {
  const { t } = useTranslation();

  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return '';
    return format(dateObj, 'yyyy/MM/dd');
  };

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          cursor: 'pointer',
          position: 'relative',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        {/* Action buttons overlay */}
        <Box sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 10,
          display: 'flex',
          gap: 0.5
        }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onClone(book);
            }}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { bgcolor: 'white' }
            }}
            title={t('common.clone')}
          >
            <CloneIcon fontSize="small" color="info" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(book.id);
            }}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { bgcolor: 'white' }
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(book);
            }}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': { bgcolor: 'white' }
            }}
          >
            <DeleteIcon fontSize="small" color="error" />
          </IconButton>
        </Box>

        <Box 
          sx={{ height: 200, position: 'relative', bgcolor: 'grey.100' }}
          onClick={() => onEdit(book.id)}
        >
          <CardMedia
            component="img"
            height="200"
            image={getImageUrl(book.imageUrl, book.title)}
            alt={book.title}
            sx={{ 
              objectFit: 'cover',
              width: '100%'
            }}
          />
        </Box>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
            {book.title}
          </Typography>
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {book.author}
          </Typography>
          
          {book.series && (
            <Typography variant="body2" color="primary.main" gutterBottom sx={{ fontStyle: 'italic' }}>
              {book.series}
            </Typography>
          )}
          
          <Box sx={{ mb: 2 }}>
            {book.publisher && (
              <Chip 
                label={book.publisher} 
                size="small" 
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            )}
            {book.genre && (
              <Chip 
                label={book.genre} 
                size="small" 
                variant="outlined"
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            )}
            {book.type && (
              <Chip 
                label={book.type} 
                size="small" 
                variant="outlined"
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            )}
            {book.language && (
              <Chip 
                label={book.language} 
                size="small" 
                variant="outlined"
                sx={{ mr: 0.5, mb: 0.5 }}
              />
            )}
          </Box>

          <Box sx={{ mt: 'auto' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {book.isbn && `ISBN: ${book.isbn}`}
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                {userCurrency} {Math.round(book.price || 0).toLocaleString()}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('common.quantity')}: {book.qty || 1}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(book.createdDate)}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
}

export default BookCard; 